// src/app/protected-route.tsx
import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { routeOwnerStorage } from '@/auth/route-owner';
import { needsOnboarding } from '@/auth/roles';
import { LoginPage } from '@/auth/login-page';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { isQuietLoadingRoute } from '@/shared/lib/quiet-loading-route';

interface ProtectedRouteProps {
  children: ReactNode;
}

/** Gate order mirrors Flutter getInitialRoute:
 *  unauthenticated → login; authenticated + needsOnboarding → onboarding stub; else app. */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const routeOwnerChanged = useAuthStore((s) => s.routeOwnerChanged);

  if (status === 'unknown') {
    // PROD-3194: an encounter link opened cold shows no spinner at any stage, this gate included.
    return isQuietLoadingRoute(window.location.pathname) ? (
      <QuietLoading className="h-screen" label="Loading your session" />
    ) : (
      <BrandLoading variant="page" label="Loading your session" />
    );
  }
  if (status === 'unauthenticated') {
    return <LoginPage />;
  }
  if (needsOnboarding(user)) {
    // Onboarding wizard is Phase 6 — placeholder gate keeps the contract explicit.
    // A stranded/degraded session (e.g. a 409 that loaded JWT-only data without
    // isOnboarded) must always be able to escape back to a fresh sign-in, so the
    // gate carries a Sign out action — otherwise the only recovery is clearing
    // localStorage by hand (PROD-1298).
    return (
      <FullScreenMessage text="Provider onboarding required (wizard ships in a later phase)." showSignOut />
    );
  }
  // Another person signed in on the very page the last person's session ended on (an expired session keeps its
  // URL): that route is not theirs, so they start at home. A different URL is a link and stays. Children wait.
  if (routeOwnerChanged) return <LeaveInheritedRoute>{children}</LeaveInheritedRoute>;
  return <TrackRouteOwner userId={user?.userId}>{children}</TrackRouteOwner>;
}

/** The auth storage keys (storage.ts): a change to one of them in another tab is a session change there. */
const AUTH_KEYS = ['scrips.access_token', 'scrips.refresh_token', 'scrips.user', 'scrips.user_v'];

/** Add the page the signed-in person is on to the route-owner ledger (route-owner.ts). Mounted only after the
 *  inherited-route gate above, so a person is never recorded on a page that was another person's. */
function TrackRouteOwner({ userId, children }: { userId: string | undefined; children: ReactNode }) {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!userId) return;
    const path = pathname + search;
    routeOwnerStorage.addPage(userId, path);
    // A tab coming back to the front, or another tab changing the session, refreshes this page's place in the
    // ledger (it is capped, and other tabs add pages too).
    const refresh = () => routeOwnerStorage.addPage(userId, path);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || AUTH_KEYS.includes(e.key)) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId, pathname, search]);
  return <>{children}</>;
}

function LeaveInheritedRoute({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const acknowledge = useAuthStore((s) => s.acknowledgeRouteOwnerChanged);
  const atHome = pathname === '/';
  useEffect(() => {
    if (atHome) acknowledge();
    else navigate('/', { replace: true });
  }, [atHome, acknowledge, navigate]);
  return atHome ? <>{children}</> : null;
}

function FullScreenMessage({ text, showSignOut }: { text: string; showSignOut?: boolean }) {
  const logout = useAuthStore((s) => s.logout);
  return (
    <main className="grid min-h-app-screen place-items-center font-sans text-text-secondary">
      <div className="flex flex-col items-center gap-4">
        <p>{text}</p>
        {showSignOut && (
          <Button variant="secondary" onClick={logout}>
            Sign out
          </Button>
        )}
      </div>
    </main>
  );
}
